import React from 'react';
import { format, startOfDay } from 'date-fns';
import { Col, Input, Label, Row } from 'reactstrap';
import ActionButton from '../../../../../Components/Common/ActionButton';
import { SkeletonForm } from '../../../../../Components/Common/Skeleton';
import { SelectField, TextField } from '../../../../../Components/Form';
import { getMultiSelectValue } from './utils';

export const COUNTER_PORTS = [
  {
    no: 1,
    locationField: 'incotermLocationId',
    quantityField: 'quantityLocation1',
    dateFromField: 'startDate',
    dateToField: 'endDate',
  },
  {
    no: 2,
    locationField: 'incotermLocation2Id',
    quantityField: 'quantityLocation2',
    dateFromField: 'startDate2',
    dateToField: 'endDate2',
  },
  {
    no: 3,
    locationField: 'incotermLocation3Id',
    quantityField: 'quantityLocation3',
    dateFromField: 'startDate3',
    dateToField: 'endDate3',
  },
];

export const getCounterPortCount = formData => {
  if (!formData?.incotermPortType) return 0;
  if (formData.incotermPortType === 'SinglePort') return 1;
  if (formData.incotermPortType !== 'MultiPort') return 0;
  if (formData.incotermMultiPortType === '3Port') return 3;
  if (formData.incotermMultiPortType === '2Port') return 2;
  return 0;
};

export const CounterProductAlert = ({ form }) => (
  <div className='alert alert-primary bg-primary-subtle border-0 text-primary p-3 d-flex gap-2 rounded-3 mb-4 align-items-start'>
    <i className='ri-information-line fs-20 mt-0'></i>
    <div>
      <h6 className='fw-semibold mb-1 text-primary fs-14'>
        Alternative Requirement Offering
      </h6>
      <p className='small text-muted mb-0 fs-12'>
        Propose alternative delivery terms. Product must remain:{' '}
        <span className='fw-bold text-primary'>
          {[form.productName, form.productTypename].filter(Boolean).join(' ') ||
            form.productType?.name ||
            '-'}
        </span>
      </p>
    </div>
  </div>
);

export const CounterModalLoading = () => <SkeletonForm fields={6} />;

export const CounterFieldError = ({ message }) =>
  message ? (
    <div className='hy-field__error' role='alert'>
      <i className='ri-error-warning-line' aria-hidden='true' />
      <span>{message}</span>
    </div>
  ) : null;

export const CounterModalFooter = ({
  loading,
  saveDisabled = false,
  onCancel,
  onSave,
  formData,
}) => (
  <div className='border-top p-3 bg-white d-flex flex-wrap justify-content-end gap-2'>
    <ActionButton
      variant='secondary'
      icon='ri-close-line'
      onClick={onCancel}
      disabled={loading}
    >
      Cancel
    </ActionButton>
    <ActionButton
      variant='primary'
      icon='ri-save-line'
      onClick={() => onSave(formData)}
      loading={loading}
      disabled={saveDisabled}
      title={saveDisabled ? 'Counter data must differ from original data' : ''}
    >
      Save
    </ActionButton>
  </div>
);

const todayInputDate = () => format(startOfDay(new Date()), 'yyyy-MM-dd');

const toDateInputString = value => {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return format(value, 'yyyy-MM-dd');
  }
  const raw = String(value).trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const slash = raw.split('/');
  if (slash.length === 3) {
    const [day, month, year] = slash;
    if (year && month && day) {
      return `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  }
  return '';
};

const FieldError = ({ message }) =>
  message ? (
    <div className='text-danger d-flex align-items-center gap-1 mt-1 fs-12'>
      <i className='ri-error-warning-line'></i>
      <span>{message}</span>
    </div>
  ) : null;

export const CounterDeliveryBasisFields = ({ formData, onFormChange }) => (
  <TextField
    label='Delivery basis'
    name='deliveryBasis'
    value={formData.deliveryBasis}
    onChange={e => onFormChange('deliveryBasis', e.target.value)}
    hint='Enter the total volume as a number, for example 100.'
  />
);

export const CounterPortPick = ({
  portCount = 0,
  error,
  onPortSetup,
  maxPorts = 3,
}) => (
  <div className='mb-3'>
    <div
      className={[
        'pd-portpick',
        portCount === 0 ? 'pd-portpick--empty' : '',
        error ? 'pd-portpick--invalid' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className='pd-portpick__label'>
        How many discharge ports? <span className='text-danger'>*</span>
        <small>
          {portCount === 0
            ? 'Choose one before filling in the ports.'
            : `A delivery can have at most ${maxPorts} discharge locations.`}
        </small>
      </div>
      <div
        id='incotermPortType'
        className='pd-portpick__options'
        role='group'
        aria-label='Number of discharge ports'
        tabIndex={-1}
      >
        {[1, 2, 3].map(count => (
          <button
            key={count}
            type='button'
            className='pd-portpick__btn'
            aria-pressed={portCount === count}
            onClick={() => onPortSetup?.(count)}
          >
            <span className='pd-portpick__pins' aria-hidden='true'>
              {Array.from({ length: count }, (_, i) => (
                <i key={i} className='ri-map-pin-2-fill'></i>
              ))}
            </span>
            {count === 1 ? 'Single port' : `${count} ports`}
          </button>
        ))}
      </div>
    </div>
    {error ? (
      <div className='text-danger d-flex align-items-center gap-1 mt-1'>
        <i className='ri-error-warning-line'></i>
        <span>{error}</span>
      </div>
    ) : null}
  </div>
);

export const CounterDateRange = ({
  label,
  dateFromField,
  dateToField,
  dateFromValue,
  dateToValue,
  required = true,
  errors = {},
  onFormChange,
}) => {
  const minFrom = todayInputDate();
  const fromYmd = toDateInputString(dateFromValue);
  const toYmd = toDateInputString(dateToValue);
  const hasFrom = Boolean(fromYmd);
  const minTo = fromYmd || undefined;

  return (
    <>
      <Label className='form-label'>
        {label}
        {required ? <span className='text-danger'> *</span> : null}
      </Label>
      <div className='pd-date-range'>
        <div>
          <Input
            type='date'
            id={dateFromField}
            name={dateFromField}
            aria-label={`${label} from`}
            className='form-control'
            invalid={Boolean(errors[dateFromField])}
            min={minFrom}
            value={fromYmd}
            onChange={e => {
              let nextFrom = e.target.value;
              if (nextFrom && nextFrom < minFrom) nextFrom = minFrom;
              onFormChange(dateFromField, nextFrom);
              if (!nextFrom) {
                onFormChange(dateToField, '');
                return;
              }
              if (toYmd && toYmd < nextFrom) {
                onFormChange(dateToField, nextFrom);
              }
            }}
          />
          <FieldError message={errors[dateFromField]} />
        </div>
        <span className='pt-2 text-muted fs-12'>to</span>
        <div>
          <Input
            type='date'
            id={dateToField}
            name={dateToField}
            aria-label={`${label} to`}
            className='form-control'
            invalid={Boolean(errors[dateToField])}
            min={minTo}
            value={hasFrom ? toYmd : ''}
            disabled={!hasFrom}
            onChange={e => {
              if (!hasFrom) return;
              let nextTo = e.target.value;
              if (nextTo && nextTo < minTo) nextTo = minTo;
              onFormChange(dateToField, nextTo);
            }}
          />
          <FieldError message={errors[dateToField]} />
        </div>
      </div>
    </>
  );
};

export const CounterPortBlock = ({
  portNo = 1,
  title,
  locationField,
  locationValue,
  multiSelectField,
  multiSelectValue,
  isMultiSelect = false,
  dateFromField,
  dateFromValue,
  dateToField,
  dateToValue,
  dateLabel = 'Discharge dates',
  quantityField,
  quantityValue,
  showQuantity = false,
  incotermLocationOptions = [],
  onFormChange,
  errors = {},
}) => (
  <div className='pd-port'>
    <div className='pd-port__head'>
      <span
        className={`pd-port__swatch pd-swatch-${portNo}`}
        aria-hidden='true'
      ></span>
      {title || `Port ${portNo}`}
    </div>
    <Row className='g-3'>
      <Col md={showQuantity ? 4 : 5}>
        {isMultiSelect ? (
          <SelectField
            label='Locations'
            name={multiSelectField || locationField}
            multi
            required
            options={incotermLocationOptions}
            value={getMultiSelectValue(
              incotermLocationOptions,
              multiSelectValue
            ).map(option => option.value)}
            onChange={ids =>
              onFormChange(multiSelectField || locationField, ids)
            }
            error={errors[multiSelectField || locationField]}
          />
        ) : (
          <SelectField
            label='Location'
            name={locationField}
            required
            options={incotermLocationOptions}
            value={locationValue}
            onChange={value => onFormChange(locationField, value ?? '')}
            error={errors[locationField]}
          />
        )}
      </Col>
      {showQuantity ? (
        <Col md={3}>
          <TextField
            label='Quantity'
            name={quantityField}
            required
            value={quantityValue}
            onChange={e => onFormChange(quantityField, e.target.value)}
            error={errors[quantityField]}
          />
        </Col>
      ) : null}
      <Col md={showQuantity ? 5 : 7}>
        <CounterDateRange
          label={dateLabel}
          dateFromField={dateFromField}
          dateToField={dateToField}
          dateFromValue={dateFromValue}
          dateToValue={dateToValue}
          errors={errors}
          onFormChange={onFormChange}
        />
      </Col>
    </Row>
  </div>
);

export const CounterDischargePorts = ({
  formData,
  portCount,
  showQuantity = false,
  incotermLocationOptions,
  onFormChange,
  errors = {},
}) => {
  const visibleCount = Math.max(portCount || 0, 1);
  return (
    <div className='mt-2'>
      {COUNTER_PORTS.slice(0, visibleCount).map(port => (
        <CounterPortBlock
          key={port.no}
          portNo={port.no}
          locationField={port.locationField}
          locationValue={formData[port.locationField]}
          dateFromField={port.dateFromField}
          dateFromValue={formData[port.dateFromField]}
          dateToField={port.dateToField}
          dateToValue={formData[port.dateToField]}
          quantityField={port.quantityField}
          quantityValue={formData[port.quantityField]}
          showQuantity={showQuantity}
          dateLabel='Discharge dates'
          incotermLocationOptions={incotermLocationOptions}
          onFormChange={onFormChange}
          errors={errors}
        />
      ))}
    </div>
  );
};
